import { Badge, type BadgeTone } from '@/components/ui/Badge';

/**
 * Reifegrad einer Routine - eine Stelle für Label, Ton und Bedeutung.
 *
 * Bis 13.08.2026 stand bei allen zehn Routinen "experimental", weil der
 * Registry-Upsert das Wort als Konstante hineinschrieb. Es sagte nichts aus und
 * klebte trotzdem an jeder Zeile der Sammel-Vorschau. Zwei Konsequenzen, beide
 * hier abgebildet:
 *
 *  1. Der Wert kommt jetzt aus dem Playbook-Meta-Header (`tier`) und bedeutet
 *     etwas - nämlich, wie viel ERPROBUNG belegt ist.
 *  2. Angezeigt wird er nur, wo er etwas aussagt: `production` ist der
 *     Normalfall und bekommt kein Badge. Ein Hinweis, der überall steht, wird
 *     nirgends gelesen.
 */

export type RoutineTier = 'experimental' | 'beta' | 'production' | 'deprecated';

export const TIER_LABEL: Record<RoutineTier, string> = {
  experimental: 'experimentell',
  beta: 'beta',
  production: 'produktiv',
  deprecated: 'veraltet',
};

/**
 * Was die Stufe BEDEUTET - als Tooltip an jedem Badge. Bewusst in der Sprache
 * des Belegs formuliert ("belegt"/"kein Beleg") und nicht als Schulnote: die
 * Stufe sagt, was wir gesehen haben, nicht wie gut jemand die Routine findet.
 */
export const TIER_MEANING: Record<RoutineTier, string> = {
  experimental:
    'Experimentell: bisher kein erfolgreicher Lauf auf einem echten System belegt. Kann funktionieren, kann fehlschlagen - beim Start zuschauen.',
  beta: 'Beta: läuft auf echten Systemen an, ein sauberer Durchlauf ist aber noch nicht belegt.',
  production:
    'Produktiv: mindestens ein erfolgreicher Durchlauf auf einem echten System ist belegt.',
  deprecated: 'Veraltet: wird nicht mehr gepflegt, es gibt einen Nachfolger.',
};

const TIER_TONE: Record<RoutineTier, BadgeTone> = {
  // Warnfarbe nur für die Stufe, die wirklich eine Warnung ist.
  experimental: 'warning',
  // Beta ist ein Hinweis, keine Warnung - sonst sieht wieder alles nach Alarm aus.
  beta: 'primary',
  production: 'success',
  deprecated: 'neutral',
};

const KNOWN_TIERS: readonly RoutineTier[] = [
  'experimental',
  'beta',
  'production',
  'deprecated',
];

/** Server-String auf eine bekannte Stufe abbilden; `null` = unbekannter Wert. */
export function normalizeTier(raw: string | null | undefined): RoutineTier | null {
  const value = (raw ?? '').trim().toLowerCase();
  return (KNOWN_TIERS as readonly string[]).includes(value)
    ? (value as RoutineTier)
    : null;
}

export function tierLabel(raw: string | null | undefined): string {
  const tier = normalizeTier(raw);
  // Unbekanntes roh zeigen statt schlucken: lieber ein fremdes Wort im UI als
  // eine Stufe, die das Backend nie gemeint hat.
  return tier ? TIER_LABEL[tier] : (raw ?? '').trim() || 'unbekannt';
}

export function tierMeaning(raw: string | null | undefined): string {
  const tier = normalizeTier(raw);
  return tier
    ? TIER_MEANING[tier]
    : `Unbekannte Reifegrad-Stufe "${(raw ?? '').trim() || '-'}" - das Backend liefert einen Wert, den diese Oberfläche nicht kennt.`;
}

interface RoutineTierBadgeProps {
  tier: string | null | undefined;
  'data-testid'?: string;
}

/**
 * `exactOptionalPropertyTypes` erlaubt kein `data-testid={undefined}` - das
 * Attribut muss dann ganz fehlen. Deshalb hier gespreizt statt durchgereicht.
 */
function testIdProp(testId: string | undefined): { 'data-testid'?: string } {
  return testId === undefined ? {} : { 'data-testid': testId };
}

/**
 * Badge für den Reifegrad - NUR für die Stufen, die etwas aussagen.
 * `production` ist der erwartete Normalfall und bekommt bewusst nichts:
 * eine Marke, die an jeder Zeile hängt, liest am Ende niemand mehr.
 */
export function RoutineTierBadge({
  tier,
  'data-testid': testId,
}: RoutineTierBadgeProps): JSX.Element | null {
  const known = normalizeTier(tier);
  if (known === 'production') return null;

  return (
    <span title={tierMeaning(tier)}>
      <Badge tone={known ? TIER_TONE[known] : 'neutral'} {...testIdProp(testId)}>
        {tierLabel(tier)}
      </Badge>
    </span>
  );
}

interface RoutineTierCellProps {
  tier: string | null | undefined;
  'data-testid'?: string;
}

/**
 * Zellinhalt der Reifegrad-Spalte in der Library.
 *
 * Hier - und nur hier - bekommt auch `production` Text: die Spalte gehört dem
 * Feld, eine leere Zelle läse sich nicht von "Daten fehlen" unterscheiden.
 * Aber eben als stiller Grauton, nicht als grünes Häkchen.
 */
export function RoutineTierCell({
  tier,
  'data-testid': testId,
}: RoutineTierCellProps): JSX.Element {
  const known = normalizeTier(tier);
  if (known === 'production') {
    return (
      <span className="text-xs text-muted" title={tierMeaning(tier)} data-testid={testId}>
        {TIER_LABEL.production}
      </span>
    );
  }
  return (
    <span title={tierMeaning(tier)}>
      <Badge tone={known ? TIER_TONE[known] : 'neutral'} {...testIdProp(testId)}>
        {tierLabel(tier)}
      </Badge>
    </span>
  );
}

interface RoutineTierHintProps {
  tier: string | null | undefined;
  'data-testid'?: string;
}

/**
 * Dezente Variante für die Sammel-Vorschau: dort trägt die Zeile schon
 * Routinenname, Code, "destruktiv", "Snapshot" und ggf. "bewusst waehlen" -
 * ein weiteres farbiges Badge wäre genau das Rauschen, das hier weg soll.
 * Kleine Schrift, Ton nur bei `experimental`, Bedeutung im Tooltip.
 */
export function RoutineTierHint({
  tier,
  'data-testid': testId,
}: RoutineTierHintProps): JSX.Element | null {
  const known = normalizeTier(tier);
  if (known === 'production') return null;

  const emphasized = known === 'experimental' || known === null;
  return (
    <span
      className={`text-xs ${emphasized ? 'text-warning' : 'text-muted'}`}
      title={tierMeaning(tier)}
      data-testid={testId}
    >
      {tierLabel(tier)}
    </span>
  );
}
